import React, { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Card, Headline, Input, InputLabel, Flex, Button } from "_styleguide";
import { Button as BarnButton, message } from "@centrate-io/barn";
import { formatExpiration, NumbersOnly } from "_assets/js/helpers";
import { getErrors } from "_helpers/api";
import { saveCreditCard } from "_graphql/mutations";
import {
  detectCardType,
  getCardProperties,
  getCardTypeLabel,
} from "../_helpers";
import { validateCreditCard } from "../_helpers/validation";
import { useSettings } from "_contexts/useSettings";
import { isDarkColor } from "_helpers/colors";
import "./CreditCard.scss";

function CreditCard(props) {
  const client = useApolloClient();
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardZip, setCardZip] = useState("");
  const [cardType, setCardType] = useState("");

  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  const handleCardNumberChange = (e) => {
    const value = NumbersOnly(e.target.value);
    setCardNumber(value);
    setCardType(detectCardType(value));
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiration(e.target.value);
    setCardExpiry(formatted);
  };

  const handleCvvChange = (e) => {
    const value = NumbersOnly(e.target.value);
    setCardCvv(value);
  };

  const handleZipChange = (e) => {
    const value = NumbersOnly(e.target.value);
    setCardZip(value);
  };

  const form = {
    cardNumber,
    cardName,
    cardExpiry,
    cardCvv,
    cardZip,
  };

  const validation = validateCreditCard(form, cardType);
  const isValid = validation?.allValid ? true : false;

  const handleSave = async () => {
    if (!isValid) return;

    setSaving(true);

    const formData = {
      application_id: props.applicationId,
      issuer: getCardTypeLabel(cardType) || "Credit Card",
      ccName: cardName,
      ccNumber: cardNumber,
      ccExp: cardExpiry,
      ccCvc: cardCvv,
    };

    try {
      await client.mutate({
        variables: formData,
        mutation: saveCreditCard,
      });
      message.success("Credit card saved successfully");
      if (props.onSave) props.onSave();
    } catch (err) {
      message.error(getErrors(err) || "Error saving credit card, try again");
      setSaving(false);
    }
  };

  const handleBack = () => {
    props.setSelectedMethod(undefined);
  };

  return (
    <Card id="payment-information-cc">
      <div className="apply-form">
        <Headline>Credit Card</Headline>
        <div className="pim-credit-card" style={{ marginBottom: "24px" }}>
          <Flex vertical className="card-number-container">
            <InputLabel htmlFor="cardNumber">
              Card Number *{" "}
              {cardType && (
                <span className="card-type-label">
                  {getCardTypeLabel(cardType)?.toUpperCase()}
                </span>
              )}
            </InputLabel>
            <Input
              id="cardNumber"
              value={cardNumber}
              onChange={handleCardNumberChange}
              placeholder="1234 5678 9012 3456"
              size="medium"
              maxLength={getCardProperties(cardType).maxLength}
              disabled={saving}
              status={
                validation.cardNumber.started && !validation.cardNumber.valid
                  ? "error"
                  : ""
              }
            />
          </Flex>

          <Flex vertical>
            <InputLabel htmlFor="cardName">Name on Card *</InputLabel>
            <Input
              id="cardName"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              placeholder="John Smith"
              size="medium"
              disabled={saving}
              status={
                validation.cardName.started && !validation.cardName.valid
                  ? "error"
                  : ""
              }
            />
          </Flex>

          <Flex align="flex-start" gap="12px">
            <Flex vertical flex={1}>
              <InputLabel htmlFor="cardExpiry">Expiration *</InputLabel>
              <Input
                id="cardExpiry"
                value={cardExpiry}
                onChange={handleExpiryChange}
                placeholder="MM/YY"
                size="large"
                maxLength="5"
                disabled={saving}
                status={
                  validation.cardExpiry.started && !validation.cardExpiry.valid
                    ? "error"
                    : ""
                }
              />
            </Flex>

            <Flex vertical flex={1}>
              <InputLabel htmlFor="cardCvv">CVV *</InputLabel>
              <Input
                id="cardCvv"
                value={cardCvv}
                onChange={handleCvvChange}
                placeholder={cardType === "amex" ? "1234" : "123"}
                size="large"
                maxLength={getCardProperties(cardType).cvvLength}
                type="password"
                disabled={saving}
                status={
                  validation.cardCvv.started && !validation.cardCvv.valid
                    ? "error"
                    : ""
                }
              />
            </Flex>

            <Flex vertical flex={1}>
              <InputLabel htmlFor="cardZip">Zip Code *</InputLabel>
              <Input
                id="cardZip"
                value={cardZip}
                onChange={handleZipChange}
                placeholder="12345"
                size="large"
                maxLength="5"
                disabled={saving}
                status={
                  validation.cardZip.started && !validation.cardZip.valid
                    ? "error"
                    : ""
                }
              />
            </Flex>
          </Flex>
        </div>

        <div className="payment-buttons">
          <Flex gap="12px">
            <BarnButton
              type="secondary"
              size="large"
              onClick={handleBack}
              disabled={saving}
              style={{ flex: 1 }}
            >
              Back
            </BarnButton>
            <Button
              block
              size="large"
              style={{
                background: primaryColor,
                color: primaryFontColor,
                flex: 1,
              }}
              onClick={handleSave}
              disabled={!isValid}
              loading={saving}
            >
              Save Credit Card
            </Button>
          </Flex>
        </div>
      </div>
    </Card>
  );
}

export default CreditCard;
